
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAISettings, availableProviders, Resource } from '@/hooks/useAISettings';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { 
  AlertTriangle, 
  Eye, 
  EyeOff, 
  Save, 
  TestTube, 
  Info, 
  Plus, 
  Link as LinkIcon, 
  FileText, 
  Trash2, 
  ExternalLink 
} from 'lucide-react';
import { toast } from 'sonner';
import PageTransition from '@/components/PageTransition';
import { sendChatMessage } from '@/lib/aiProvider';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AISettings = () => {
  const navigate = useNavigate();
  const { settings, updateSettings, addResource, removeResource, isLoading } = useAISettings();
  const [localSettings, setLocalSettings] = useState(settings);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  
  // Resources states
  const [newResource, setNewResource] = useState({
    name: '',
    type: 'link' as 'link' | 'pdf',
    url: '',
    description: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Check if the user is authenticated as admin
    const authData = localStorage.getItem('adminAuth');
    if (!authData || !JSON.parse(authData).authenticated) {
      toast.error('You must be logged in as admin to access this page');
      navigate('/login');
    }
  }, [navigate]);

  // Update local settings when the loaded settings change
  useEffect(() => {
    if (!isLoading) {
      setLocalSettings(settings);
    }
  }, [settings, isLoading]);

  // Get models for the selected provider
  const selectedProvider = availableProviders.find((p) => p.id === localSettings.provider);
  const availableModels = selectedProvider?.models || [];
  
  const handleChange = (field: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    updateSettings(localSettings);
    
    toast.success('AI settings saved successfully', {
      duration: 3000,
      dismissible: true
    });
  };

  const handleAddResource = () => {
    if (!newResource.name.trim()) {
      toast.error('Resource name is required');
      return;
    }
    
    if (!newResource.url.trim()) {
      toast.error('Resource URL is required');
      return;
    }
    
    // Validate URL format
    try {
      new URL(newResource.url);
    } catch (e) {
      toast.error('Please enter a valid URL');
      return;
    }
    
    // Convert to AIResource format
    const aiResource = {
      id: Date.now().toString(),
      name: newResource.name,
      content: '', // Empty for URL resources
      type: newResource.type as 'link' | 'pdf' | 'document' | 'website' | 'api',
      url: newResource.url,
      description: newResource.description,
      dateAdded: new Date().toISOString(),
    };
    
    addResource(aiResource);
    
    toast.success('Resource added successfully');
    
    // Reset form
    setNewResource({
      name: '',
      type: 'link',
      url: '',
      description: ''
    });
  };

  const handleRemoveResource = (id: string) => {
    removeResource(id);
    toast.success('Resource removed');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // For this demo, we'll just simulate PDF file upload and create a resource URL
    // In a real implementation, you would upload this to a storage service and get back a URL
    
    // Create a fake URL for demonstration purposes
    const fakeUrl = `https://storage.example.com/pdfs/${encodeURIComponent(file.name)}`;
    
    setNewResource(prev => ({
      ...prev,
      name: file.name,
      type: 'pdf',
      url: fakeUrl
    }));
    
    toast.info('For this demo, we simulate the upload. In production, the file would be uploaded to storage.');
  };

  const handleTest = async () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }

    // Check for API key if required
    if (!localSettings.apiKey && selectedProvider?.requiresApiKey) {
      setTestError(`API key required for ${selectedProvider?.name}`);
      toast.error(`Please enter an API key for ${selectedProvider?.name}`);
      return;
    }

    setIsTesting(true);
    setTestError(null);
    setTestResponse('');
    setIsOfflineMode(false);
    
    try {
      // Create a dummy message for testing
      const dummyUserMessage = {
        id: 'test',
        content: testMessage,
        isUser: true,
        timestamp: new Date()
      };

      // Use actual API call
      const response = await sendChatMessage([], dummyUserMessage, localSettings);
      setTestResponse(response.message);
      toast.success('Test successful!');
    } catch (error) {
      console.error('Error testing AI settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setTestError(errorMessage);
      
      // Parse common error messages to be more user-friendly
      if (errorMessage.includes('quota') || errorMessage.includes('insufficient_quota')) {
        toast.error('API quota exceeded. Check your billing details or try another provider.', {
          duration: 5000,
          dismissible: true
        });
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('timeout')) {
        setIsOfflineMode(true);
        toast.error('Our AI assistant is currently busy. We\'ll contact you once available.', {
          duration: 8000,
          dismissible: true
        });
        
        // Set a more helpful error message
        setTestError('Network connectivity issue detected. The application will show a contact form when network issues occur.');
        
        // Add sample response explaining the fallback behavior
        setTestResponse("When network issues occur, users will see a contact form like: \"Thanks for your note. I'm currently busy assisting other customers. Please leave your contact info and I'll message you as soon as I'm free!\"");
      } else if (errorMessage.includes('API key')) {
        toast.error('Invalid API key. Please check your API key and try again.', {
          duration: 5000,
          dismissible: true
        });
      } else {
        toast.error(`Error: ${errorMessage}`, {
          duration: 5000,
          dismissible: true
        });
      }
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <AdminSidebar />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading AI settings...</p>
          </div>
        </div>
      </div>
    );
  }

  // Make sure resources is defined with a fallback to empty array
  const resources = localSettings.resources || [];

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">Admin - AI Chat Agent Settings</h1>
              <Button onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </Button>
            </div>
            
            <Separator className="mb-6" />

            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Settings</CardTitle>
                  <CardDescription>Configure basic chat agent settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="enabled">Enable AI Chat Agent</Label>
                    <Switch
                      id="enabled"
                      checked={localSettings.enabled}
                      onCheckedChange={(checked) => handleChange('enabled', checked)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="initialMessage">Welcome Message</Label>
                    <Textarea
                      id="initialMessage"
                      value={localSettings.initialMessage}
                      onChange={(e) => handleChange('initialMessage', e.target.value)}
                      placeholder="Enter the initial message shown to users"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>AI Model Configuration</CardTitle>
                  <CardDescription>Select and configure the AI provider and model</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="provider">AI Provider</Label>
                    <Select
                      value={localSettings.provider}
                      onValueChange={(value) => {
                        handleChange('provider', value);
                        // Reset model when provider changes
                        const provider = availableProviders.find(p => p.id === value);
                        const firstModel = provider?.models[0] || '';
                        handleChange('model', firstModel);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProviders.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="model">AI Model</Label>
                    <Select
                      value={localSettings.model}
                      onValueChange={(value) => handleChange('model', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        {availableModels.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="apiKey">API Key</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Input
                      id="apiKey"
                      type={showApiKey ? 'text' : 'password'}
                      value={localSettings.apiKey || ''}
                      onChange={(e) => handleChange('apiKey', e.target.value)}
                      placeholder={`Enter your ${selectedProvider?.name} API Key`}
                    />
                    {selectedProvider?.requiresApiKey && (
                      <p className="text-xs text-muted-foreground">
                        This will be stored securely. For production use, consider storing API keys in a server-side environment.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Knowledge Resources</CardTitle>
                  <CardDescription>Manage resources that help the AI provide accurate information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <Label>Current Resources ({resources.length})</Label>
                    
                    {resources.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-20" />
                        <p>No resources added yet</p>
                        <p className="text-xs mt-1">Add links or documents to help the AI answer questions accurately</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {resources.map((resource) => (
                          <div key={resource.id} className="flex items-start justify-between border-b pb-3">
                            <div className="flex items-start gap-3">
                              {resource.type === 'link' ? (
                                <LinkIcon className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
                              ) : (
                                <FileText className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{resource.name}</h4>
                                  <a 
                                    href={resource.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-500 hover:underline"
                                  >
                                    <ExternalLink className="h-3 w-3 inline" />
                                  </a>
                                </div>
                                {resource.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  Added: {new Date(resource.dateAdded).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveResource(resource.id)}
                              className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="w-full">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Resource
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>Add Knowledge Resource</DialogTitle>
                      </DialogHeader>
                      <Tabs defaultValue="link" className="mt-4">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="link">Link</TabsTrigger>
                          <TabsTrigger value="pdf">PDF Document</TabsTrigger>
                        </TabsList>
                        <TabsContent value="link" className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="resourceName">Resource Name</Label>
                            <Input
                              id="resourceName"
                              placeholder="e.g., Emergency Planning Guide"
                              value={newResource.name}
                              onChange={(e) => setNewResource(prev => ({ ...prev, name: e.target.value, type: 'link' }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="resourceUrl">URL</Label>
                            <Input
                              id="resourceUrl"
                              placeholder="https://example.com/guide"
                              value={newResource.url}
                              onChange={(e) => setNewResource(prev => ({ ...prev, url: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="resourceDescription">Description (Optional)</Label>
                            <Textarea
                              id="resourceDescription"
                              placeholder="Brief description of this resource"
                              value={newResource.description}
                              onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                              rows={3}
                            />
                          </div>
                        </TabsContent>
                        <TabsContent value="pdf" className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label htmlFor="pdfUpload">Upload PDF Document</Label>
                            <div className="border-2 border-dashed border-border rounded-md p-6 text-center">
                              <FileText className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                              <p className="text-sm mb-2">Drag & drop or click to upload</p>
                              <p className="text-xs text-muted-foreground mb-4">Maximum file size: 10MB</p>
                              <Button 
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                              >
                                Select PDF
                              </Button>
                              <input 
                                type="file" 
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".pdf"
                                className="hidden"
                              />
                              {newResource.name && newResource.type === 'pdf' && (
                                <p className="text-sm text-muted-foreground mt-4">
                                  Selected: {newResource.name}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="pdfDescription">Description (Optional)</Label>
                            <Textarea
                              id="pdfDescription"
                              placeholder="Brief description of this document"
                              value={newResource.description}
                              onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                              rows={3}
                            />
                          </div>
                          <div className="bg-muted p-3 rounded-md mt-2">
                            <div className="flex">
                              <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                              <p className="text-xs">
                                In this demo, PDF uploads are simulated. In a production environment, 
                                files would be uploaded to secure storage.
                              </p>
                            </div>
                          </div>
                        </TabsContent>
                      </Tabs>
                      <DialogFooter className="mt-6">
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddResource}>Add Resource</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Advanced Settings</CardTitle>
                  <CardDescription>Configure response behavior and style</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="systemPrompt">System Prompt</Label>
                    <Textarea
                      id="systemPrompt"
                      value={localSettings.systemPrompt}
                      onChange={(e) => handleChange('systemPrompt', e.target.value)}
                      placeholder="Enter instructions for the AI"
                      rows={5}
                    />
                    <p className="text-xs text-muted-foreground">
                      The system prompt determines the AI's personality, behavior, and objectives. Be specific about how you want it to sell your services.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="temperature" className="flex items-center justify-between">
                      <span>Temperature: {localSettings.temperature.toFixed(1)}</span>
                    </Label>
                    <Slider
                      id="temperature"
                      min={0}
                      max={1}
                      step={0.1}
                      value={[localSettings.temperature]}
                      onValueChange={(value) => handleChange('temperature', value[0])}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>More predictable (0.0)</span>
                      <span>More creative (1.0)</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxTokens">Response Length (max tokens)</Label>
                    <Input
                      id="maxTokens"
                      type="number"
                      min={100}
                      max={4000}
                      value={localSettings.maxTokens}
                      onChange={(e) => handleChange('maxTokens', parseInt(e.target.value) || 1000)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Test AI Response</CardTitle>
                  <CardDescription>Send a test message to see how the AI responds</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="testMessage">Test Message</Label>
                    <Textarea
                      id="testMessage"
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Enter a message to test the AI response"
                      rows={2}
                    />
                  </div>

                  {testError && (
                    <Card className="border-destructive">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-destructive">Error</p>
                            <p>{testError}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {testResponse && (
                    <Card className="bg-muted/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">AI Response</CardTitle>
                        {isOfflineMode && (
                          <div className="bg-amber-100 text-amber-800 px-3 py-2 rounded-md text-xs">
                            <AlertTriangle className="h-3 w-3 inline-block mr-1" />
                            Showing contact form behavior
                          </div>
                        )}
                      </CardHeader>
                      <CardContent>
                        <p className="whitespace-pre-wrap">{testResponse}</p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
                <CardFooter>
                  <Button onClick={handleTest} className="w-full" disabled={isTesting}>
                    {isTesting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-background border-t-transparent rounded-full mr-2"></div>
                        Testing...
                      </>
                    ) : (
                      <>
                        <TestTube className="mr-2 h-4 w-4" />
                        Test Response
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default AISettings;
